// src/components/auth/SignInForm.js
// Filepath: // src/components/auth/SignInForm.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import GoogleIcon from "../../assets/google-button-icon.png";
import AppleIcon from "../../assets/apple-button-icon.png";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { sendPasswordResetEmail } from "../../services/authService";

const SignInForm = ({
  handleEmailPasswordSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  loading,
  error,
}) => {
  const navigate = useNavigate();

  // Local state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  // Forgot password handler with Snackbar alerts
  const handleForgotPassword = async () => {
    if (!email) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please enter your email address first",
      });
      return;
    }
    try {
      await sendPasswordResetEmail(email);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Password reset email sent. Please check your inbox.",
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to send password reset email. Please try again later.",
      });
    }
  };

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Provider buttons */}
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              onClick={handleGoogleSignIn}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                fontSize: "1.1rem",
                fontWeight: "normal",
              }}
            >
              <img
                src={GoogleIcon}
                alt="Google"
                style={{ width: "35px", position: "absolute", left: "8px" }}
              />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                {loading ? "Signing in..." : "Sign in with Google"}
              </Box>
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button
              onClick={handleAppleSignIn}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                fontSize: "1.1rem",
                fontWeight: "normal",
              }}
            >
              <img
                src={AppleIcon}
                alt="Apple"
                style={{ width: "24px", position: "absolute", left: "12px" }}
              />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                {loading ? "Signing in..." : "Sign in with Apple"}
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Email/password section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" sx={{ mb: 2 }} align="center">
          Or sign in with email
        </Typography>

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              variant="outlined"
              autoComplete="email"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              variant="outlined"
              autoComplete="current-password"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} edge="end" sx={{ pr: 2 }}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              onClick={() => handleEmailPasswordSignIn(email, password)}
              variant="contained"
              fullWidth
              disabled={loading || !email || !password}
              sx={{
                fontSize: "1.1rem",
                fontWeight: "normal",
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            {/* more space between the button and the links */}
            <Box sx={{ mt: 3 }} />

            <Typography align="center" sx={{ my: 2 }}>
              <Link onClick={handleForgotPassword} underline="always"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": {
                    cursor: "pointer",
                    textDecoration: "underline",
                  },
                }}
              >
                Forgot your password?
              </Link>
            </Typography>

            <Typography align="center">
              Don't have Google or Apple?
            </Typography>

            <Typography align="center">
              <Link onClick={() => navigate("/signup")} underline="always"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": {
                    cursor: "pointer",
                    textDecoration: "underline",
                  },
                }}
              >
                Sign up with your email address.
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar for forgot-password flow */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignInForm;
